const VAPID_PUBLIC_KEY = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;

interface PushSubscriptionData {
  endpoint: string | undefined;
  p256dh: string | undefined;
  auth: string | undefined;
}

interface PushSupportDiagnostics {
  supported: boolean;
  reason: string | null;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  if (!base64String || typeof base64String !== 'string') {
    throw new Error('Clé VAPID invalide ou absente (valeur: ' + String(base64String) + ')');
  }
  const cleaned = base64String.trim();
  if (!/^[A-Za-z0-9\-_=+/]+$/.test(cleaned)) {
    throw new Error('La clé VAPID contient des caractères invalides');
  }
  const padding = '='.repeat((4 - (cleaned.length % 4)) % 4);
  const base64 = (cleaned + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    process.env.NODE_ENV === 'production' &&
    !!VAPID_PUBLIC_KEY
  );
}

export function getPushSupportDiagnostics(): PushSupportDiagnostics {
  if (typeof window === 'undefined') {
    return {
      supported: false,
      reason: 'Le push n\'est disponible que dans le navigateur.',
    };
  }

  if (!window.isSecureContext) {
    return {
      supported: false,
      reason: 'Les notifications push nécessitent un contexte sécurisé (HTTPS ou localhost).',
    };
  }

  if (!('serviceWorker' in navigator)) {
    return {
      supported: false,
      reason: 'Ce navigateur ne supporte pas les service workers.',
    };
  }

  if (!('PushManager' in window)) {
    return {
      supported: false,
      reason: 'Ce navigateur ne supporte pas les notifications push.',
    };
  }

  if (process.env.NODE_ENV !== 'production') {
    return {
      supported: false,
      reason: 'Les notifications push sont desactivees en mode developpement. Utilisez une build de production pour les tester.',
    };
  }

  if (!VAPID_PUBLIC_KEY) {
    return {
      supported: false,
      reason: 'La clé publique VAPID du frontend est absente.',
    };
  }

  return {
    supported: true,
    reason: null,
  };
}

export async function getPermissionState(): Promise<NotificationPermission | 'unsupported'> {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
}

export async function subscribeToPush(): Promise<PushSubscriptionData> {
  if (!isPushSupported()) {
    const diagnostics = getPushSupportDiagnostics();
    throw new Error(diagnostics.reason || 'Push notifications non supportées');
  }

  console.log('[Push] Début subscribeToPush, permission actuelle:', Notification.permission);

  if (Notification.permission === 'denied') {
    throw new Error('Les notifications sont bloquées pour ce site. Réactivez-les dans les paramètres du site (icône cadenas/ⓘ dans la barre d\'adresse).');
  }

  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
    console.log('[Push] Permission obtenue:', permission);
  } catch (permError) {
    console.error('[Push] Erreur requestPermission:', permError);
    throw new Error('Erreur lors de la demande de permission: ' + (permError as Error).message);
  }

  if (permission === 'denied') {
    throw new Error('Les notifications sont bloquées pour ce site. Réactivez-les dans les paramètres du site (icône cadenas/ⓘ dans la barre d\'adresse).');
  }

  if (permission !== 'granted') {
    throw new Error('La demande de permission a été ignorée ou fermée sans validation.');
  }

  let registration: ServiceWorkerRegistration | undefined;
  try {
    registration = await navigator.serviceWorker.getRegistration();
    console.log('[Push] Registration existante:', !!registration);
    if (!registration) {
      console.log('[Push] Enregistrement du service worker /sw.js ...');
      registration = await navigator.serviceWorker.register('/sw.js');
      console.log('[Push] Service worker enregistré');
    }
  } catch (swError) {
    console.error('[Push] Erreur service worker:', swError);
    throw new Error('Le service worker n\'a pas pu être enregistré. Essayez de recharger la page. (' + (swError as Error).message + ')');
  }

  let readyRegistration: ServiceWorkerRegistration;
  try {
    // Timeout on navigator.serviceWorker.ready (can hang on some mobile browsers)
    readyRegistration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Le service worker ne répond pas. Rechargez la page et réessayez.')), 8000)
      ),
    ]);
    console.log('[Push] Service worker ready, scope:', readyRegistration.scope);
  } catch (readyError) {
    console.error('[Push] Erreur SW ready:', readyError);
    throw readyError;
  }

  const existingSubscription = await readyRegistration.pushManager.getSubscription();
  if (existingSubscription) {
    console.log('[Push] Subscription existante trouvée');
    const existingJson = existingSubscription.toJSON();
    return {
      endpoint: existingJson.endpoint,
      p256dh: existingJson.keys?.p256dh,
      auth: existingJson.keys?.auth,
    };
  }

  let applicationServerKey: Uint8Array;
  try {
    applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY!);
  } catch (keyError) {
    console.error('[Push] Erreur clé VAPID:', keyError);
    throw new Error('Configuration serveur push invalide (clé VAPID). Contactez l\'administrateur.');
  }

  let subscription: PushSubscription;
  try {
    subscription = await readyRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
    console.log('[Push] Nouvelle subscription créée');
  } catch (subError) {
    console.error('[Push] Erreur subscribe:', subError);
    throw new Error('Impossible de s\'abonner aux notifications push: ' + (subError as Error).message);
  }

  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
  };
}

export async function unsubscribeFromPush(): Promise<string | null> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await subscription.unsubscribe();
    return subscription.endpoint;
  }
  return null;
}

export async function getPushSubscription(): Promise<PushSubscriptionData | null> {
  if (!isPushSupported()) return null;
  try {
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Service worker timeout')), 5000)
      ),
    ]);
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return null;
    const json = subscription.toJSON();
    return {
      endpoint: json.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    };
  } catch (err) {
    console.warn('[Push] getPushSubscription error:', err);
    return null;
  }
}
